(() => {
  if (window.__resinSocietyInteractionsLoaded) return;
  window.__resinSocietyInteractionsLoaded = true;

  const body = document.body;
  if (!body) return;

  const finePointer = window.matchMedia('(pointer: fine)').matches;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const interactiveSelector = 'a, button, summary, input, textarea, select, [role="button"], .card, .product-card, .collection-card, .resin-home-tile, .resin-home-product-card, .resin-home-journal-card, .resin-home-trust-card';
  const addToCartSelector = 'form[action*="/cart/add"] button, form[action*="/cart/add"] [type="submit"], button[name="add"], [type="submit"][name="add"], .product-form__submit, [data-add-to-cart], [data-cart-add], [aria-label*="Add to cart"], [aria-label*="add to cart"]';

  const initScrollReveals = () => {
    if (reduceMotion || !('IntersectionObserver' in window)) {
      document.documentElement.classList.add('rs-scroll-reveal-disabled');
      return;
    }

    const main = document.getElementById('MainContent');
    if (!main) return;

    const revealSelector = [
      '.resin-home-trust-card',
      '.resin-home-department-card',
      '.resin-home-product-card',
      '.resin-home-journal-card',
      '.resin-home__section-head .resin-home__eyebrow',
      '.resin-home__section-head h2',
      '.resin-home__section-head p:not(.resin-home__eyebrow)',
      '.resin-home__section-head > a',
      '.resin-home-section-header',
      '.resin-home-products__header',
      '.resin-home-journal__header',
      '.rs-collection-doorway',
      '.rs-collection-doorways__heading',
      '.section-header',
      '.section__header',
      '.rich-text__wrapper'
    ].join(',');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-rs-revealed');
        window.setTimeout(() => entry.target.classList.add('is-rs-reveal-settled'), 1300);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -9% 0px', threshold: 0.08 });

    const pending = [];
    let flushScheduled = false;

    const flushPending = () => {
      flushScheduled = false;
      const batch = pending.splice(0);
      const aboveFold = batch.map(({ element }) => element.getBoundingClientRect().top < window.innerHeight * 0.92);

      batch.forEach(({ element, delay }, index) => {
        element.classList.add('rs-scroll-reveal');
        element.style.setProperty('--rs-reveal-delay', `${delay}ms`);
        delete element.dataset.rsRevealQueued;
        if (aboveFold[index]) {
          element.classList.add('is-rs-revealed', 'is-rs-reveal-settled');
        } else {
          observer.observe(element);
        }
      });
    };

    const register = (element, delay = 0) => {
      if (!element || element.classList.contains('rs-scroll-reveal') || element.dataset.rsRevealQueued === 'true') return;
      if (element.closest('motion-list')) return;
      element.dataset.rsRevealQueued = 'true';
      pending.push({ element, delay });
      if (!flushScheduled) {
        flushScheduled = true;
        queueMicrotask(flushPending);
      }
    };

    const scan = (root = main) => {
      const sections = root === main
        ? Array.from(main.querySelectorAll(':scope > .shopify-section'))
        : Array.from(root.matches?.('.shopify-section') ? [root] : root.querySelectorAll?.('.shopify-section') || []);

      sections.forEach((section, sectionIndex) => {
        if (sectionIndex === 0 && section.parentElement === main) return;

        const candidates = Array.from(section.querySelectorAll(revealSelector))
          .filter((element) => !element.parentElement?.closest(revealSelector));

        if (candidates.length) {
          candidates.forEach((element, itemIndex) => register(element, Math.min(itemIndex, 5) * 65));
          return;
        }

        const sectionContent = section.firstElementChild;
        if (sectionContent && !sectionContent.matches('script, style')) register(sectionContent);
      });

      if (root !== main && root.matches?.(revealSelector)) register(root);
      root.querySelectorAll?.(revealSelector).forEach((element, index) => register(element, Math.min(index, 5) * 65));
    };

    scan();
    document.documentElement.classList.add('rs-scroll-reveal-enabled');

    if ('MutationObserver' in window) {
      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) scan(node);
        }));
      });
      mutationObserver.observe(main, { childList: true, subtree: true });
    }
  };

  initScrollReveals();

  const initProductRailPaging = () => {
    const rails = Array.from(document.querySelectorAll('[data-rs-product-rail]'));
    rails.forEach((rail) => {
      const cards = Array.from(rail.querySelectorAll('.resin-home-product-card'));
      const indicator = rail.parentElement
        ? rail.parentElement.querySelector('[data-rs-swipe-indicator]')
        : null;
      const current = indicator ? indicator.querySelector('[data-rs-swipe-current]') : null;
      if (!cards.length) return;

      let frame = 0;
      const update = () => {
        frame = 0;
        const railCenter = rail.scrollLeft + rail.clientWidth / 2;
        let activeIndex = 0;
        let closest = Infinity;

        cards.forEach((card, index) => {
          const cardCenter = card.offsetLeft + card.offsetWidth / 2;
          const distance = Math.abs(cardCenter - railCenter);
          if (distance < closest) {
            closest = distance;
            activeIndex = index;
          }
        });

        cards.forEach((card, index) => card.classList.toggle('is-rs-active-slide', index === activeIndex));
        if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
      };

      const requestUpdate = () => {
        if (!frame) frame = window.requestAnimationFrame(update);
      };

      rail.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
      update();
    });
  };

  initProductRailPaging();

  const initJournalRailPaging = () => {
    const rails = Array.from(document.querySelectorAll('[data-rs-journal-rail]'));
    rails.forEach((rail) => {
      const cards = Array.from(rail.querySelectorAll('.resin-home-journal-card'));
      const indicator = rail.parentElement
        ? rail.parentElement.querySelector('[data-rs-journal-swipe-indicator]')
        : null;
      const current = indicator ? indicator.querySelector('[data-rs-journal-swipe-current]') : null;
      if (!cards.length) return;

      let frame = 0;
      const update = () => {
        frame = 0;
        const railCenter = rail.scrollLeft + rail.clientWidth / 2;
        let activeIndex = 0;
        let closest = Infinity;

        cards.forEach((card, index) => {
          const cardCenter = card.offsetLeft + card.offsetWidth / 2;
          const distance = Math.abs(cardCenter - railCenter);
          if (distance < closest) {
            closest = distance;
            activeIndex = index;
          }
        });

        cards.forEach((card, index) => card.classList.toggle('is-rs-active-slide', index === activeIndex));
        if (current) current.textContent = String(activeIndex + 1).padStart(2, '0');
      };

      const requestUpdate = () => {
        if (!frame) frame = window.requestAnimationFrame(update);
      };

      rail.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate, { passive: true });
      update();
    });
  };

  initJournalRailPaging();

  const initDesktopRailControls = () => {
    const controls = Array.from(document.querySelectorAll('[data-rs-desktop-rail-controls]'));
    controls.forEach((control) => {
      const rail = control.previousElementSibling;
      const previous = control.querySelector('[data-rs-rail-previous]');
      const next = control.querySelector('[data-rs-rail-next]');
      if (!rail || !previous || !next) return;

      const update = () => {
        const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
        previous.disabled = rail.scrollLeft <= 2;
        next.disabled = rail.scrollLeft >= maxScroll - 2;
      };
      const move = (direction) => rail.scrollBy({
        left: direction * rail.clientWidth,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });

      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      rail.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  };

  initDesktopRailControls();


  let audioContext;
  let masterGain;
  let compressor;
  let lastHoverSound = 0;
  let lastAddToCartSound = 0;

  const ensureAudio = () => {
    if (audioContext) return audioContext;
    const AudioCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtor) return null;

    audioContext = new AudioCtor();
    compressor = audioContext.createDynamicsCompressor();
    compressor.threshold.value = -28;
    compressor.knee.value = 16;
    compressor.ratio.value = 2.4;
    compressor.attack.value = 0.01;
    compressor.release.value = 0.22;

    masterGain = audioContext.createGain();
    masterGain.gain.value = 0.12;
    masterGain.connect(compressor);
    compressor.connect(audioContext.destination);
    return audioContext;
  };

  const resumeAudio = () => {
    const ctx = ensureAudio();
    if (ctx && ctx.state === 'suspended') ctx.resume();
    return ctx;
  };

  const tone = ({ frequency, start, duration, type = 'sine', gainValue = 0.03, endFrequency, filterFrequency = 780, destination = masterGain }) => {
    const ctx = audioContext;
    const oscillator = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    const gain = ctx.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(40, endFrequency || frequency), start + duration);
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(filterFrequency, start);
    filter.Q.value = 0.5;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(gainValue, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.04);
  };

  const addToCartBurst = (event) => {
    if (reduceMotion) return;
    const source = event && 'clientX' in event ? event : null;
    const x = source ? source.clientX : window.innerWidth - 96;
    const y = source ? source.clientY : 96;
    for (let i = 0; i < 16; i += 1) {
      const spark = document.createElement('span');
      spark.className = 'rs-cart-spark';
      const angle = (Math.PI * 2 * i) / 16 + Math.random() * 0.22;
      const distance = 26 + Math.random() * 36;
      spark.style.left = `${x}px`;
      spark.style.top = `${y}px`;
      spark.style.setProperty('--rs-spark-x', `${Math.cos(angle) * distance}px`);
      spark.style.setProperty('--rs-spark-y', `${Math.sin(angle) * distance}px`);
      spark.style.setProperty('--rs-spark-delay', `${Math.random() * 70}ms`);
      document.body.appendChild(spark);
      window.setTimeout(() => spark.remove(), 920);
    }
  };

  const playHover = () => {
    const nowMs = performance.now();
    if (nowMs - lastHoverSound < 180) return;
    lastHoverSound = nowMs;
    const ctx = resumeAudio();
    if (!ctx) return;
    const now = ctx.currentTime;
    tone({ frequency: 392, endFrequency: 440, start: now, duration: 0.08, type: 'sine', gainValue: 0.018, filterFrequency: 620 });
    tone({ frequency: 587.33, endFrequency: 523.25, start: now + 0.018, duration: 0.09, type: 'triangle', gainValue: 0.012, filterFrequency: 760 });
  };

  const playClick = () => {
    const ctx = resumeAudio();
    if (!ctx) return;
    const now = ctx.currentTime;
    tone({ frequency: 220, endFrequency: 196, start: now, duration: 0.11, type: 'triangle', gainValue: 0.03, filterFrequency: 540 });
    tone({ frequency: 330, endFrequency: 294, start: now + 0.02, duration: 0.12, type: 'sine', gainValue: 0.018, filterFrequency: 700 });
  };

  const playAddToCart = (event) => {
    const nowMs = performance.now();
    if (nowMs - lastAddToCartSound < 420) return;
    lastAddToCartSound = nowMs;
    const ctx = resumeAudio();
    if (!ctx) return;
    const now = ctx.currentTime + 0.006;

    tone({ frequency: 164.81, endFrequency: 146.83, start: now, duration: 0.14, type: 'triangle', gainValue: 0.028, filterFrequency: 480 });
    tone({ frequency: 246.94, endFrequency: 220, start: now + 0.03, duration: 0.16, type: 'sine', gainValue: 0.02, filterFrequency: 620 });
    tone({ frequency: 329.63, endFrequency: 392, start: now + 0.11, duration: 0.2, type: 'sine', gainValue: 0.018, filterFrequency: 720 });
    tone({ frequency: 523.25, endFrequency: 659.25, start: now + 0.2, duration: 0.18, type: 'triangle', gainValue: 0.014, filterFrequency: 820 });
    addToCartBurst(event);
  };

  const isAddToCartTarget = (target) => {
    if (!target || !target.closest) return false;
    const direct = target.closest(addToCartSelector);
    if (direct) return true;
    const button = target.closest('button, [type="submit"], [role="button"], a');
    if (!button) return false;
    const label = `${button.textContent || ''} ${button.getAttribute('aria-label') || ''}`.toLowerCase();
    return label.includes('add to cart') || label.includes('add cart') || label.includes('add bag');
  };

  if (finePointer && !reduceMotion) {
    document.addEventListener('mouseover', (event) => {
      if (event.target.closest(interactiveSelector)) playHover();
    });

    document.addEventListener('pointerdown', (event) => {
      if (isAddToCartTarget(event.target)) playAddToCart(event);
      else if (event.target.closest(interactiveSelector)) playClick();
    }, { passive: true, capture: true });

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form && form.matches && form.matches('form[action*="/cart/add"]')) playAddToCart(event);
    }, true);
  }


  const reviewModals = Array.from(document.querySelectorAll('[data-rs-review-modal]'));
  reviewModals.forEach((modal) => {
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
  });

  const getTargetReviewModal = (trigger) => {
    const href = trigger && trigger.getAttribute ? trigger.getAttribute('href') : '';
    if (href && href.charAt(0) === '#') {
      const escaped = href.slice(1).replace(/([ #;&,.+*~':"!^$[\]()=>|/@])/g, '\\$1');
      const target = document.querySelector(`#${escaped}`);
      if (target && target.matches('[data-rs-review-modal]')) return target;
    }
    return reviewModals[0] || null;
  };

  const openJudgeMeReviews = (event) => {
    const trigger = event.target && event.target.closest ? event.target.closest('[data-rs-judgeme-open]') : null;
    if (!trigger) return;
    const reviewModal = getTargetReviewModal(trigger);
    if (!reviewModal) return;
    event.preventDefault();
    reviewModals.forEach((modal) => { modal.hidden = true; });
    reviewModal.hidden = false;
    body.classList.add('rs-review-modal-open');
  };

  const closeJudgeMeReviews = () => {
    reviewModals.forEach((modal) => { modal.hidden = true; });
    body.classList.remove('rs-review-modal-open');
  };

  document.addEventListener('click', openJudgeMeReviews);
  document.addEventListener('click', (event) => {
    if (event.target && event.target.closest && event.target.closest('[data-rs-review-close]')) closeJudgeMeReviews();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && reviewModals.some((modal) => !modal.hidden)) closeJudgeMeReviews();
  });

  const treeCounters = document.querySelectorAll('[data-rs-tree-counter]');
  if (treeCounters.length) {
    const formatCount = (value, original) => {
      if (original && original.includes(',')) return Math.round(value).toLocaleString();
      return String(Math.round(value));
    };
    const animateTreeCounter = (counter) => {
      if (counter.dataset.rsCounted === 'true') return;
      const raw = counter.getAttribute('data-count') || counter.textContent || '0';
      const numeric = parseInt(raw.replace(/[^0-9]/g, ''), 10);
      if (!Number.isFinite(numeric) || numeric <= 0 || reduceMotion) return;
      counter.dataset.rsCounted = 'true';
      const start = performance.now();
      const duration = 900;
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        counter.textContent = formatCount(numeric * eased, raw);
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animateTreeCounter(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.42 }) : null;
    treeCounters.forEach((counter) => observer ? observer.observe(counter) : animateTreeCounter(counter));
  }

  if (!finePointer || reduceMotion) return;

  const cursor = document.createElement('div');
  cursor.className = 'rs-liquid-cursor';
  cursor.setAttribute('aria-hidden', 'true');
  document.body.appendChild(cursor);
  body.classList.add('rs-cursor-enabled');

  let targetX = window.innerWidth / 2;
  let targetY = window.innerHeight / 2;
  let currentX = targetX;
  let currentY = targetY;
  let lastDropX = targetX;
  let lastDropY = targetY;
  let lastDropTime = 0;
  let cursorVisible = false;

  const createDrop = (x, y, speed) => {
    const drop = document.createElement('span');
    const size = Math.max(12, Math.min(34, 14 + speed * 0.18 + Math.random() * 10));
    drop.className = 'rs-resin-drop';
    drop.style.left = `${x}px`;
    drop.style.top = `${y}px`;
    drop.style.setProperty('--rs-drop-size', `${size}px`);
    drop.style.setProperty('--rs-drop-rotate', `${Math.round(Math.random() * 140 - 70)}deg`);
    document.body.appendChild(drop);
    window.setTimeout(() => drop.remove(), 900);
  };

  const animateCursor = () => {
    currentX += (targetX - currentX) * 0.34;
    currentY += (targetY - currentY) * 0.34;
    cursor.style.transform = `translate3d(${currentX}px, ${currentY}px, 0) scale(var(--rs-cursor-scale, 1))`;
    requestAnimationFrame(animateCursor);
  };

  requestAnimationFrame(animateCursor);

  window.addEventListener('pointermove', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    targetX = event.clientX;
    targetY = event.clientY;

    if (!cursorVisible) {
      cursorVisible = true;
      cursor.classList.add('is-visible');
    }

    const dx = targetX - lastDropX;
    const dy = targetY - lastDropY;
    const distance = Math.hypot(dx, dy);
    const now = performance.now();

    if (distance > 16 && now - lastDropTime > 24) {
      createDrop(targetX - dx * 0.32, targetY - dy * 0.32, distance);
      lastDropX = targetX;
      lastDropY = targetY;
      lastDropTime = now;
    }
  }, { passive: true });

  window.addEventListener('pointerleave', () => {
    cursorVisible = false;
    cursor.classList.remove('is-visible');
  });

  document.addEventListener('pointerover', (event) => {
    if (event.target.closest(interactiveSelector)) cursor.classList.add('is-hovering');
  });

  document.addEventListener('pointerout', (event) => {
    if (event.target.closest(interactiveSelector)) cursor.classList.remove('is-hovering');
  });

  window.addEventListener('pointerdown', () => cursor.classList.add('is-down'), { passive: true });
  window.addEventListener('pointerup', () => cursor.classList.remove('is-down'), { passive: true });
})();
